import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { Box, Paper, Typography, Button, Alert } from '@mui/material'
import { Refresh } from '@mui/icons-material'
import type { WithTranslation } from 'react-i18next'
import { withTranslation } from 'react-i18next'

interface Props extends WithTranslation {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
}

class AuthErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null, errorInfo: null }
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error, errorInfo: null }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log the error to console in development
        // Note: Console messages are technical/debugging only and do not require i18n
        if (process.env.NODE_ENV === 'development') {
            console.error('[AuthErrorBoundary] Error caught:', error, errorInfo)
        }

        this.setState({
            error,
            errorInfo,
        })
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null })
    }

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default error UI
            return (
                <Box
                    sx={{
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                    }}
                >
                    <Paper elevation={2} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {this.props.t('errors:boundary.title')}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                {this.state.error?.message ||
                                    this.props.t('errors:boundary.message')}
                            </Typography>
                        </Alert>
                        <Button
                            variant="contained"
                            startIcon={<Refresh />}
                            onClick={this.handleRetry}
                            sx={{ mt: 2 }}
                        >
                            {this.props.t('errors:boundary.retry')}
                        </Button>
                    </Paper>
                </Box>
            )
        }

        return this.props.children
    }
}

const TranslatedAuthErrorBoundary = withTranslation(['errors'])(AuthErrorBoundary)
export default TranslatedAuthErrorBoundary
